import { Button } from '@components/Button'
import { HeroIcon } from '@components/HeroIcon'
import { MediaRender } from '@components/MediaRender'
import { NavigationControl } from '@components/NavigationControl'
import { IMedia } from '@dtos/Course'
import { useCurrentLesson } from '@hooks/useCurrentLesson'
import { useLessonTabStore } from '@stores/useLessonTabStore'
import { router } from 'expo-router'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, Text, View, XStack, YStack } from 'tamagui'
import { NotSupported } from '../extra_contents/NotSupported'
import { ContentsCollapsibleWeb } from './ContentsCollapsibleWeb'
import { CourseContentNavigation } from './CourseContentNavigation'
import { MediaRenderWrapper } from './MediaRenderWrapper'
import { IScreenProps } from './MobileScreen'
import { TabsContentContainer } from './TabsContentContainer'

export function DesktopScreen({
  hasPreviousContent,
  hasNextContent,
  isCollapsibleOpen,
  toggleCollapsible,
}: IScreenProps) {
  const { t } = useTranslation('CourseDetails')
  const { currentTab, setCurrentTab } = useLessonTabStore()
  const { currentLesson } = useCurrentLesson()

  const handleBack = useCallback(() => {
    router.back()
  }, [])
  return (
    <YStack flex={1} w="100%">
      <XStack
        gap={!isCollapsibleOpen ? 0 : '$spacing-inset-sm'}
        mt="$spacing-inset-lg"
        marginHorizontal={48}
      >
        <AnimatePresence presenceAffectsLayout>
          <YStack
            key="lesson-container"
            flex={1}
            maxHeight={1200}
            w="100%"
            animation="100ms"
            animateOnly={['width']}
          >
            <YStack
              backgroundColor="$neutral-0"
              p="$spacing-inset-sm"
              borderTopLeftRadius="$border-radius-md"
              borderTopRightRadius="$border-radius-md"
              gap="$spacing-inset-xs"
            >
              {/* Header */}
              <XStack
                gap="$spacing-inset-xs"
                w="100%"
                alignItems="center"
                justifyContent="space-between"
              >
                <Button
                  variant="primary"
                  aria-label="Back"
                  link
                  paddingLeft={0}
                  onPress={handleBack}
                >
                  <XStack alignItems="center">
                    <HeroIcon
                      icon={['fal', 'chevron-left']}
                      color="$primary-300"
                      size={16}
                    />
                    <Text
                      fontSize="$font-size-xxs"
                      ff="$mulishRegular"
                      color="$primary-300"
                      position="relative"
                      textDecorationLine="underline"
                    >
                      {t('Voltar')}
                    </Text>
                  </XStack>
                </Button>

                <XStack
                  minWidth={280}
                  justifyContent="flex-end"
                  animation="quick"
                  enterStyle={{
                    opacity: 0,
                    x: 20,
                  }}
                  exitStyle={{
                    opacity: 0,
                    x: 20,
                  }}
                  opacity={1}
                  x={0}
                >
                  {isCollapsibleOpen ? (
                    <View w="100%">
                      <CourseContentNavigation
                        visibilityType="top"
                        onPreviousPress={() => {}}
                        onNextPress={() => {}}
                        hasPreviousContent={hasPreviousContent}
                        hasNextContent={hasNextContent}
                      />
                    </View>
                  ) : (
                    <Button
                      variant="primary"
                      testID="header-back-button"
                      aria-label="Expand"
                      link
                      paddingLeft={0}
                      onPress={toggleCollapsible}
                    >
                      <XStack alignItems="center">
                        <HeroIcon
                          icon={['fal', 'chevron-left']}
                          color="$primary-300"
                          size={16}
                        />
                        <Text
                          fontSize="$font-size-xxs"
                          ff="$mulishRegular"
                          color="$primary-300"
                          position="relative"
                          textDecorationLine="underline"
                        >
                          {t('Expandir Lista')}
                        </Text>
                      </XStack>
                    </Button>
                  )}
                </XStack>
              </XStack>

              {/* Player Controls */}
              <XStack
                alignItems="center"
                justifyContent="space-between"
                w="100%"
                gap="$spacing-inset-xxs"
              >
                <View
                  animation="quick"
                  opacity={isCollapsibleOpen ? 0.3 : 1}
                  pointerEvents={isCollapsibleOpen ? 'none' : 'auto'}
                  enterStyle={{
                    opacity: 0,
                    scale: 0.95,
                  }}
                  exitStyle={{
                    opacity: 0,
                    scale: 0.95,
                  }}
                >
                  {!isCollapsibleOpen && (
                    <NavigationControl
                      testID="navigation-left-button"
                      action={() => {}}
                      chevronDirection="left"
                      size={24}
                      disabled={!hasPreviousContent || isCollapsibleOpen}
                      chevronColor="$cross-white"
                      width={52}
                      height={52}
                    />
                  )}
                </View>

                <XStack
                  alignItems="center"
                  justifyContent="center"
                  flex={1}
                  overflow="hidden"
                  animation="quick"
                  enterStyle={{
                    opacity: 0,
                    scale: 0.95,
                  }}
                  exitStyle={{
                    opacity: 0,
                    scale: 0.95,
                  }}
                >
                  <MediaRenderWrapper media={currentLesson?.media as IMedia}>
                    <MediaRender media={currentLesson?.media as IMedia}>
                      <NotSupported media={currentLesson?.media as IMedia} />
                    </MediaRender>
                  </MediaRenderWrapper>
                </XStack>

                <View
                  animation="quick"
                  opacity={isCollapsibleOpen ? 0.3 : 1}
                  pointerEvents={isCollapsibleOpen ? 'none' : 'auto'}
                  enterStyle={{
                    opacity: 0,
                    scale: 0.95,
                  }}
                  exitStyle={{
                    opacity: 0,
                    scale: 0.95,
                  }}
                >
                  {!isCollapsibleOpen && (
                    <NavigationControl
                      testID="navigation-right-button"
                      action={() => {}}
                      chevronDirection="right"
                      size={24}
                      disabled={!hasNextContent || isCollapsibleOpen}
                      chevronColor="$cross-white"
                      width={52}
                      height={52}
                    />
                  )}
                </View>
              </XStack>

              {/* Content General Information */}
              <YStack
                gap="$spacing-inset-sm"
                paddingHorizontal={
                  !isCollapsibleOpen ? 72 : '$spacing-inset-xxs'
                }
                animation="quick"
                enterStyle={{
                  opacity: 0,
                  y: 20,
                }}
                exitStyle={{
                  opacity: 0,
                  y: 20,
                }}
              >
                <YStack gap="$spacing-inset-sm">
                  <Text
                    color="$neutral-800"
                    fontSize="$font-size-lg"
                    fontFamily="$mulishBold"
                    fontWeight="700"
                    numberOfLines={2}
                  >
                    {currentLesson?.title || ''}
                  </Text>

                  <XStack alignItems="center" justifyContent="space-between">
                    <YStack gap="$spacing-inset-quarck" alignItems="center">
                      <HeroIcon
                        icon={['fas', 'check-circle']}
                        size={21}
                        color="$success-300"
                      />
                      <Text
                        color="$neutral-800"
                        fontSize="$font-size-sm"
                        fontFamily="$mulishRegular"
                        lineHeight={24}
                      >
                        Concluído
                      </Text>
                    </YStack>
                  </XStack>
                </YStack>
              </YStack>
            </YStack>
            <TabsContentContainer
              currentLesson={currentLesson}
              onTabChange={setCurrentTab}
              withoutTabContent={true}
            />
          </YStack>
        </AnimatePresence>

        {isCollapsibleOpen && <ContentsCollapsibleWeb type="topOpened" />}
      </XStack>
      <YStack marginHorizontal={48} flex={1} h="100%" paddingBottom={200}>
        <TabsContentContainer
          currentLesson={currentLesson}
          currentTab={currentTab}
          withoutTabContent={false}
          withoutTabs={true}
        />
      </YStack>
    </YStack>
  )
}
